/**
 * avatar.ts — the one place that decides what an avatar looks like.
 *
 * WHY THIS EXISTS. Before this file the app had **no shared avatar anything**. A
 * read-only inventory on 2026-08-19 found 76 places that draw a person's photo or
 * their initials, and among them:
 *
 *   • 7 near-identical local `Avatar` components (CRM, Access screen, Directory,
 *     Profile, Employees, Org chart, Training)
 *   • 12 copies of the same `initials()` function
 *   • 6 copies of a deterministic colour helper, across 5 different palettes and
 *     3 different hash functions
 *
 * That is the "46 bespoke table variants" pattern starting over, and it is what
 * this module and `components/ui/Avatar.tsx` exist to stop. Everything here is
 * pure, so it can be unit-tested without a DOM.
 *
 * NOTHING HERE CHANGES A COLOUR. The palettes below are the EXISTING palettes,
 * copied verbatim from the files they came from, so a call site can be moved onto
 * the shared component and still paint exactly the pixels it painted yesterday.
 * They differ from each other on purpose — reconciling them is a separate design
 * decision for Lis, not something to smuggle in behind a refactor.
 */

/**
 * Punctuation that must never become an initial.
 *
 * Deals and leads gained a header mark on 2026-08-19 and their seed is a TITLE, not
 * a person's name — "Amazon — AWS Marketplace listing" would otherwise read "A—",
 * an em dash presented as somebody's initial. A DENY-list rather than an allow-list
 * of letters, deliberately: an allow-list written for Latin would silently swallow
 * Cyrillic, Greek and CJK names, which is a far worse failure than missing an
 * unusual symbol. (ES5 target, so no `\p{P}` — same constraint as `matchCore.ts`.)
 */
const LEADING_PUNCT = /^[-–—·•*/\\|&@#+~^=<>(){}[\]"'`.,;:!?¿¡]+/;

/**
 * Initials for a display name.
 *
 * Two letters by default ("Adam Selipsky" → "AS"), one for entities that read as a
 * single mark ("Zenit Software" → "Z"). Splits on whitespace and takes the FIRST
 * letters of the first two words, which is what every copy of this function did:
 * "Mary Anne Wilson" → "MA", never "MW".
 *
 * Returns "?" for an empty name rather than an empty chip, because a blank square
 * reads as a broken image.
 */
export function initialsOf(name: string | null | undefined, letters: 1 | 2 = 2): string {
  const parts = (name ?? '')
    .split(/\s+/)
    .map(w => w.replace(LEADING_PUNCT, ''))
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (letters === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + (parts[1]?.charAt(0) ?? '')).toUpperCase();
}

/**
 * Initials from two separate columns — a contact's `first_name` / `last_name`.
 *
 * Kept distinct from `initialsOf` because the CRM stores a person's name in two
 * columns and joining them just to split them again gets "Mary Anne" / "Wilson"
 * wrong: it would read "MA" when the record plainly says M.W.
 */
export function initialsOfParts(first: string | null | undefined, last: string | null | undefined): string {
  const a = (first ?? '').trim().charAt(0);
  const b = (last ?? '').trim().charAt(0);
  return (a + b).toUpperCase() || '?';
}

/**
 * The corner radius ladder — Lis's, 2026-08-19: 22→7, 26→8, 34→11, 44→14.
 *
 * That is `size * 0.32`, and it reproduces all four of her stated pairs plus the
 * CRM drawer header's existing 36→12. Clamped at both ends: below ~19px the
 * formula rounds to a radius too small to read as anything but a square, and
 * above ~62px it grows fast enough to look circular again.
 *
 * THIS APPLIES ONLY TO AVATARS BEING CONVERTED FROM A CIRCLE. An avatar that is
 * ALREADY square-rounded keeps its exact current radius — Lis's ruling, so that
 * the migration changes nothing she did not ask to change. Those sites pass
 * `radius` explicitly rather than letting this run.
 */
export function avatarRadius(size: number): number {
  return Math.min(20, Math.max(6, Math.round(size * 0.32)));
}

/**
 * Default initials type size for a given avatar size.
 *
 * `size * 0.36` is taken from the Access screen's Avatar, which is the only one of
 * the seven that derived it rather than hard-coding a number. Floored at 9px,
 * which is where the app's smallest chips already sit. Every call site that had an
 * explicit font size passes it, so no migrated avatar changes its type.
 */
export function avatarFontSize(size: number): number {
  return Math.max(9, Math.round(size * 0.36));
}

/* ── Palettes ────────────────────────────────────────────────────────────────
 *
 * Verbatim copies. The name of each records where it came from, so a future
 * consolidation can see what it is merging instead of guessing.
 */

/** LinkedRecordCards + RecordContactsSection + CRMCompaniesPage. Six theme tokens. */
export const TINTS_TOKEN_6 = [
  'var(--p-c-sky)', 'var(--p-c-violet)', 'var(--p-c-amber)',
  'var(--p-c-mint)', 'var(--p-c-rose)', 'var(--p-accent)',
];

/** TrainingPage. Ten theme tokens. */
export const TINTS_TOKEN_10 = [
  'var(--p-c-teal)', 'var(--p-c-violet)', 'var(--p-c-amber)', 'var(--p-c-sky)',
  'var(--p-c-rose)', 'var(--p-c-mint)', 'var(--p-c-cyan)', 'var(--p-c-fuchsia)',
  'var(--p-c-orange)', 'var(--p-c-indigo)',
];

/** DirectoryPage. Raw hex — pre-dates the token system. */
export const TINTS_HEX_10 = [
  '#14b8a6', '#3b82f6', '#8b5cf6', '#f97316', '#ec4899',
  '#22c55e', '#eab308', '#06b6d4', '#ef4444', '#6366f1',
];

/** ContactsPage. Tailwind class names, not colour values — applied via className. */
export const TINTS_TAILWIND_8 = [
  'bg-teal-500', 'bg-blue-500', 'bg-purple-500', 'bg-orange-500',
  'bg-pink-500', 'bg-indigo-500', 'bg-green-500', 'bg-yellow-500',
];

/** AvatarStack. Muted washes — many overlapped chips must not shout. */
export const TINTS_STACK_WASH_6 = [
  'rgba(45, 212, 191, 0.28)', 'rgba(94, 169, 240, 0.28)', 'rgba(167, 139, 250, 0.28)',
  'rgba(251, 146, 60, 0.28)', 'rgba(74, 222, 128, 0.28)', 'rgba(232, 121, 249, 0.28)',
];

/**
 * Tint by FIRST CHARACTER — the CRM family (LinkedRecordCards, RecordContactsSection,
 * ContactsPage, CRMCompaniesPage).
 *
 * Cheap and stable, but every name starting with the same letter shares a colour.
 * That is the existing behaviour and is preserved deliberately: changing it would
 * repaint half the CRM.
 */
export function tintByFirstChar(seed: string | null | undefined, palette: string[]): string {
  return palette[((seed ?? '').charCodeAt(0) || 0) % palette.length];
}

/**
 * Tint by HASH of the whole name — the HR family (Directory, Training) and the
 * avatar stack. Spreads better than first-character across a roster of colleagues
 * whose names cluster on a few letters.
 */
export function tintByHash(seed: string | null | undefined, palette: string[]): string {
  const s = seed ?? '';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

/**
 * Tint by SIGNED hash — the avatar stack's variant, and the reason this exists as a
 * third function rather than a flag.
 *
 * `AvatarStack` accumulates with `| 0` (signed 32-bit) and then takes `Math.abs`,
 * where the two functions above accumulate with `>>> 0` (unsigned). For names whose
 * hash crosses the sign bit the two disagree, so folding them together would silently
 * repaint some of the overlapping chips on the Companies page. Same arithmetic as the
 * original, kept bug-for-bug on purpose.
 */
export function tintByHashSigned(seed: string | null | undefined, palette: string[]): string {
  const s = seed ?? '';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length];
}
